import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { HeaderActions } from '@/components/layout/HeaderActions';
import { cn } from '@/lib/utils';

export interface BreadcrumbEntry {
  label: string;
  href?: string;
  icon?: LucideIcon;
}

export interface PageHeaderProps {
  /** Page icon displayed next to the title */
  icon?: LucideIcon;
  /** Semantic color tint for the icon container */
  iconColor?: 'blue' | 'green' | 'purple' | 'orange' | 'rose' | 'indigo' | 'amber' | 'neutral';
  /** Page title */
  title?: string;
  /** Optional subtitle / description */
  description?: string;
  /** Breadcrumb trail. If provided, renders a multi-segment breadcrumb with the
   *  last entry as the current page. If omitted, renders just the title as a
   *  single breadcrumb page. */
  breadcrumbs?: BreadcrumbEntry[];
  /** Slot for page-specific action buttons (rendered after HeaderActions) */
  actions?: React.ReactNode;
  /** Slot for metadata rendered below the title row (badges, date info, etc.) */
  metadata?: React.ReactNode;
  /** Show a bottom border (default: false) */
  border?: boolean;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  description,
  breadcrumbs,
  actions,
  metadata,
  border = false,
}) => {
  const showStandaloneTitle = Boolean(breadcrumbs && breadcrumbs.length > 0 && title);

  return (
    <div
      className={`px-4 sm:px-6 md:px-8 pt-2 pb-1.5 ${border ? 'border-b border-border/60' : ''}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col gap-1 min-w-0">
          <div className="flex items-center gap-2.5">
            {/* Breadcrumb / Title */}
            {breadcrumbs && breadcrumbs.length > 0 ? (
              <Breadcrumb className="min-w-0 flex-1">
                <BreadcrumbList className="min-w-0 flex-nowrap">
                  {breadcrumbs.map((crumb, idx) => {
                    const isLast = idx === breadcrumbs.length - 1;
                    const CrumbIcon = crumb.icon;
                    return [
                      idx > 0 ? (
                        <BreadcrumbSeparator key={`${crumb.label}-separator`}>
                          <ChevronRight className="h-3.5 w-3.5" />
                        </BreadcrumbSeparator>
                      ) : null,
                      <BreadcrumbItem
                        key={crumb.label}
                        className={isLast && !showStandaloneTitle ? 'min-w-0 flex-1' : undefined}
                      >
                        {isLast ? (
                          <BreadcrumbPage className={cn(
                            showStandaloneTitle
                              ? 'text-sm font-medium text-muted-foreground'
                              : 'block min-w-0 truncate text-page-title text-foreground'
                          )}>
                            {crumb.label}
                          </BreadcrumbPage>
                        ) : (
                          <BreadcrumbLink asChild>
                            <Link
                              to={crumb.href || '#'}
                              className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors"
                            >
                              {CrumbIcon && <CrumbIcon className="h-4 w-4" />}
                              {crumb.label}
                            </Link>
                          </BreadcrumbLink>
                        )}
                      </BreadcrumbItem>,
                    ];
                  })}
                </BreadcrumbList>
              </Breadcrumb>
            ) : (
              <h1 className="text-page-title text-foreground">
                {title}
              </h1>
            )}
          </div>

          {showStandaloneTitle && (
            <h1 className="text-page-title text-foreground">
              {title}
            </h1>
          )}

          {/* Description */}
          {description && (
            <p className="text-sm text-muted-foreground mt-0.5">
              {description}
            </p>
          )}

          {/* Metadata slot */}
          {metadata && (
            <div className="mt-1">
              {metadata}
            </div>
          )}
        </div>

        {/* Right side: actions + HeaderActions */}
        <div className="flex items-center gap-3 shrink-0">
          {actions}
          <HeaderActions />
        </div>
      </div>
    </div>
  );
};

export default PageHeader;
